import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core'
import {
  bisector,
  curveCatmullRom,
  format,
  line as d3Line,
  max,
  min,
  mouse,
  range,
  scaleLinear,
  scaleOrdinal,
  scaleTime,
  select,
  selectAll,
  transpose,
} from 'd3'
import 'd3-transition'
import { toDate, addDays, isBetween, formatDate } from 'app/utils/date'
import { TranslateService } from '@ngx-translate/core'
import { Colors } from '../../config/constants'

@Component({
  standalone: false,
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss'],
})
export class LineChartComponent implements OnInit {
  _data = []
  _filteredData = []
  defualtConfig = {
    categoryKey: '',
    getDateLimits: () => [],
    colors: [],
  }
  _config: any = { ...this.defualtConfig }
  categories = []
  selectedCategory = null
  tooltipData = {
    x: 0,
    label: '',
    date: '',
    hidden: true,
    points: [],
  }
  colorScale = null

  @Input() interactivity = true
  @Input() unit: string

  @ViewChild('svg', { static: true }) svg: ElementRef

  @Input()
  get config() {
    return this._config
  }

  set config(newConfig) {
    newConfig = newConfig || {}
    this._config = { ...this.defualtConfig, ...newConfig }
  }

  @Input()
  get data() {
    return this._filteredData
  }

  set data(value: any[]) {
    if (!value || !value.length) {
      return
    }
    this._data = this.prepareData(value)
    if (this.config.excludeMode) {
      this.selectedCategory = this.categories[0] && this.categories[0].value
    }
    this._filteredData = this.filterData(this._data)
    this.draw()
  }

  get firstLine() {
    return this.data[0] || []
  }

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    if (this.data.length) {
      this.draw()
    }
  }

  hasSelectedCategory() {
    return this.selectedCategory !== null && this.selectedCategory !== undefined
  }

  filterData(data) {
    const filteredData = this.hasSelectedCategory()
      ? data.filter((d) => d[0].category === this.selectedCategory)
      : data
    const granularity = this.config.getCurrentGranularity()
    const dateLimits = this.config.getDateLimits()
    const pad = granularity === 'day' ? 0 : 1
    return filteredData.map((item) => {
      return item.filter((d) => {
        return isBetween(
          toDate(d.date),
          addDays(toDate(dateLimits[0]), -pad),
          addDays(toDate(dateLimits[1]), pad),
        )
      })
    })
  }

  prepareData(newData: any[]) {
    // TODO: mover el .reverse() al ORDER BY de la query
    const categories = new Set()
    const matrix = newData
      .slice()
      .reverse()
      .map((delivery) =>
        delivery.values.map((val) => {
          const category = val[this.config.categoryKey]
          categories.add(category)
          val.category = category
          val.delivery_name = this.config.deliveryNames[delivery.delivery_id]
          val.date = new Date(delivery.start)
          val.value = val.value || 0
          return val
        }),
      )
    this.categories = Array.from(categories).map((c: string) => ({
      value: c,
      label: this.config.categoryNames[c] || c,
    }))
    return transpose(matrix)
  }

  onCategorySelected(cat: string) {
    if (this.interactivity) {
      this.selectedCategory = cat
      this._filteredData = this.filterData(this._data)
      this.draw()
    }
  }

  draw() {
    const svg = select(this.svg.nativeElement)
    const svgWidth = svg.style('width').replace('px', '')
    const svgHeight = svg.style('height').replace('px', '')
    const margin = { top: 16, right: 24, bottom: 56, left: 24 }
    const width = svgWidth - margin.left - margin.right
    const height = svgHeight - margin.top - margin.bottom
    svg.selectAll('g').remove()
    const g = svg
      .append('g')
      .attr('class', 'center')
      .attr('transform', `translate(${margin.left}, ${margin.top})`)

    const colorScale = scaleOrdinal()
      .range(this.config.colors)
      .domain(range(this.data.length))
    this.colorScale = colorScale

    const dateLimits = this.config.getDateLimits()
    const xMargin = 60
    const xScale = scaleTime()
      .domain(dateLimits)
      .range([xMargin, width - xMargin])
      .clamp(true)

    const yMax = max(this.data, (d) => max(d, (d2) => d2.value))
    const yMin = min(this.data, (d) => min(d, (d2) => d2.value))
    const yScale = scaleLinear().domain([yMin, yMax]).nice().range([height, 0])

    const lineGen = d3Line()
      .curve(curveCatmullRom)
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.value))

    const tipLine = g.append('line')

    const bgGapPercent = 0.25
    g.append('g')
      .attr('class', 'y-background')
      .selectAll('line')
      .data(range(0, 1 + bgGapPercent, bgGapPercent))
      .enter()
      .append('line')
      .attr('stroke', Colors.colorN15)
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', (d) => d * height)
      .attr('y2', (d) => d * height)

    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${height})`)
      .selectAll('text')
      .data(xScale.ticks(this.config.getTickInterval()))
      .enter()
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('x', (d) => xScale(d))
      .attr('dy', '20px')
      .text((d) => {
        const granularity = this.config.getCurrentGranularity()
        const locale = this.translateService.currentLang
        const date = granularity === 'week' ? addDays(d, 1) : new Date(d)
        let opts: Intl.DateTimeFormatOptions = {
          day: '2-digit',
          month: 'short',
        }
        if (date.getDate() === 1) {
          opts =
            date.getMonth() === 0
              ? { month: 'short', year: 'numeric' }
              : { month: 'short' }
        }
        const label = formatDate(date, opts, locale)
        return label.charAt(0).toUpperCase() + label.slice(1).replace('.', '')
      })

    g.append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(${width}, 0)`)
      .selectAll('text')
      .data([yMax, yMin])
      .enter()
      .append('text')
      .attr('text-anchor', 'end')
      .attr('y', (d, i) => i * height)
      .attr('dy', '-6px')
      .html(
        (d) => `${format(',.0f')(d)}
                   <tspan class="light">
                  ${
                    this.config.showTotalUnit
                      ? this.translateService.instant(this.unit)
                      : ''
                  }
                  </tspan>`,
      )

    // To group lines, points, and labels
    const dataGroup = g
      .append('g')
      .attr('class', 'data-group')
      .attr('z-index', 1)

    if (this.interactivity) {
      const tipRect = g
        .append('rect')
        .attr('class', 'hover-rect')
        .attr('x', 0)
        .attr('width', width)
        .attr('height', height)
        .attr('opacity', 0)
        .attr('z-index', -1)
        .on('mouseout', () => {
          this.tooltipData.hidden = true
          tipLine.attr('stroke', 'none')
        })
        .on('mousemove', () => {
          const mousex = mouse(tipRect.node())[0]
          const mouseDate = xScale.invert(mousex)
          const dateBisector = bisector((d) => d.date).right
          const index = dateBisector(this.firstLine, mouseDate)
          const pointRight = this.firstLine[index]
          const pointLeft = this.firstLine[index - 1]

          if (!pointLeft || !pointRight) {
            this.tooltipData.hidden = true
            tipLine.attr('stroke', 'none')
            return
          }

          const useRight =
            mousex - xScale(pointLeft.date) > xScale(pointRight.date) - mousex
          const point = useRight ? pointRight : pointLeft
          const x = xScale(point.date)

          tipLine
            .attr('stroke', Colors.colorN20)
            .attr('x1', x)
            .attr('x2', x)
            .attr('y1', 0)
            .attr('y2', height)

          const formatter = format(',.0f')
          const pointsIndex = useRight ? index : index - 1
          this.tooltipData.points = this.data
            .map((d, i) => {
              const val = d[pointsIndex]
              return (
                val && {
                  value_raw: val.value,
                  value: formatter(val.value),
                  color: colorScale(i),
                  label: val.name,
                }
              )
            })
            .filter(Boolean)
            .sort((a, b) => b.value_raw - a.value_raw)
          this.tooltipData.label = this.translateService.instant(
            point.delivery_name,
          )
          this.tooltipData.date = point.date.toLocaleDateString()
          this.tooltipData.x = x
          this.tooltipData.hidden = false
        })
    }

    const singleLineGroup = []
    this.data.map((item, i) => {
      singleLineGroup[i] = dataGroup
        .append('g')
        .attr('class', 'single-line-group')
        .on('mouseover', (d, idx, nodes) => {
          if (this.interactivity) {
            // keep tooltip visible
            this.tooltipData.hidden = false
            tipLine.attr('stroke', Colors.colorN20)

            selectAll('.single-line-group')
              .transition()
              .duration(200)
              .attr('opacity', 0.2)
            select(nodes[idx]).transition().duration(200).attr('opacity', 1)
          }
        })
        .on('mouseout', () => {
          if (this.interactivity) {
            selectAll('.single-line-group')
              .transition()
              .duration(200)
              .attr('opacity', 1)
          }
        })

      const path = singleLineGroup[i]
        .append('path')
        .attr('pointer-events', 'visibleStroke')
        .attr('d', lineGen(item))
        .attr('stroke', 'blue')
        .attr('stroke-width', 1)
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .attr('stroke', (d) => colorScale(i))
        .attr('fill', 'none')

      const pointSize = 6
      const points = singleLineGroup[i]
        .append('g')
        .attr('class', 'points')
        .attr('fill', (d) => colorScale(i))
        .attr('stroke-width', 1)
        .attr('stroke', 'rgba(255, 255, 255, 0.4)')
        .selectAll('rect')
        .data(item)
        .enter()
        .append('rect')
        .attr('width', pointSize)
        .attr('height', pointSize)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr(
          'transform',
          (d) =>
            `translate(${xScale(d.date) - pointSize * 0.5}, ${
              yScale(d.value) - pointSize * 0.5
            })`,
        )
    })

    if (!this.config.printMode) {
      const savedY = []
      const labelsSize = 12
      const item = this.data.map((el) => el[el.length - 1])
      const getY = (index) => {
        return savedY[index] || yScale(item[index].value)
      }
      singleLineGroup[singleLineGroup.length - 1]
        .append('g')
        .attr('class', 'line-labels')
        .selectAll('.label')
        .data(item)
        .enter()
        .append('text')
        .attr('class', 'label')
        .attr('x', (d) => xScale(d.date))
        .attr('y', (d, idx) => {
          let amountToAdd = 0,
            amountToRemove = 0,
            res = yScale(d.value)
          if (
            item[idx + 1] &&
            Math.abs(yScale(d.value) - getY(idx + 1)) <= labelsSize
          ) {
            for (let i = idx + 1; i < this.data.length; i++) {
              amountToRemove +=
                item[i] && Math.abs(getY(idx) - getY(i)) <= labelsSize
                  ? labelsSize / 3
                  : 0
              amountToRemove +=
                item[i + 1] &&
                item[idx + i + 1] &&
                Math.abs(getY(idx + i) - getY(idx + i + 1)) <= labelsSize
                  ? labelsSize / 2 + labelsSize / 6
                  : 0
            }
            res = yScale(d.value) - amountToRemove
          }
          if (
            item[idx - 1] &&
            Math.abs(yScale(d.value) - getY(idx - 1)) <= labelsSize
          ) {
            for (let i = idx; i >= 0; i--) {
              amountToAdd +=
                item[i] && Math.abs(getY(idx) - getY(i)) <= labelsSize
                  ? labelsSize / 3 + 1
                  : 0
            }
            res = yScale(d.value) + amountToAdd
          }

          if (
            item[idx - 1] &&
            Math.abs(yScale(d.value) - getY(idx - 1)) <= labelsSize &&
            item[idx + 1] &&
            Math.abs(yScale(d.value) - getY(idx + 1)) <= labelsSize
          ) {
            res = yScale(d.value)
          }
          savedY[idx] = res
          return res
        })
        .attr('dy', 4)
        .attr('dx', 8)
        .text((d) =>
          this.translateService.instant(
            d.name || this.categories.find((c) => c.value === d.category).label,
          ),
        )
    }
  }
}
